import lamejs from './lame.all.js'

class WebmToMp3Converter {
  /**
   * Convert a WebM blob to MP3
   * @param {Blob} webmBlob - The input WebM blob
   * @param {Object} [options] - Conversion options
   * @param {number} [options.bitRate=128] - MP3 bit rate
   * @param {number} [options.maxFileSizeMB=50] - Maximum file size in MB
   * @returns {Promise<Blob>} - Promise resolving to MP3 blob
   */
  static async convert (webmBlob, options = {}) {
    return new Promise((resolve, reject) => {
      // File size check
      const { bitRate = 128 } = options

      // Validate lamejs is available
      if (typeof lamejs === 'undefined') {
        reject(new Error('lamejs library is not loaded. Please include lame.min.js before this script'))
        return
      }

      // Create audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()

      // Create file reader
      const reader = new FileReader()

      reader.onload = async (e) => {
        try {
          // Decode the audio data with a dynamic timeout based on file size
          // For long recordings (30+ mins), allow more time: ~1 second per MB, minimum 30 seconds
          const arrayBuffer = e.target.result
          const fileSizeMB = arrayBuffer.byteLength / (1024 * 1024)
          const timeoutMs = Math.max(30000, Math.ceil(fileSizeMB * 1000)) // At least 30 seconds, or 1s per MB

          console.log('Decoding audio with timeout:', {
            fileSizeMB: fileSizeMB.toFixed(2),
            timeoutSeconds: (timeoutMs / 1000).toFixed(0)
          })

          const audioBuffer = await Promise.race([
            audioContext.decodeAudioData(arrayBuffer),
            new Promise((resolve, reject) =>
              setTimeout(() => reject(new Error(`Audio decoding timed out after ${(timeoutMs / 1000).toFixed(0)} seconds. File may be too large.`)), timeoutMs)
            )
          ])

          // Prepare samples and determine number of channels
          const channels = audioBuffer.numberOfChannels
          const sampleRate = audioBuffer.sampleRate

          console.log('Audio Buffer Details:', {
            channels,
            sampleRate,
            duration: audioBuffer.duration,
            durationMinutes: (audioBuffer.duration / 60).toFixed(2),
            fileSize: webmBlob.size,
            fileSizeMB: (webmBlob.size / (1024 * 1024)).toFixed(2)
          })

          // Prepare float samples
          const floatSamples = []
          for (let i = 0; i < channels; i++) {
            floatSamples.push(audioBuffer.getChannelData(i))
          }

          // Convert to Int16 samples with yielding to prevent UI freeze
          const int16Samples = []
          const samplesPerYield = 100000 // Yield every 100k samples to keep UI responsive

          for (let ch = 0; ch < channels; ch++) {
            const floatChannel = floatSamples[ch]
            const int16 = new Int16Array(floatChannel.length)

            for (let j = 0; j < floatChannel.length; j++) {
              // Convert float to 16-bit integer
              int16[j] = Math.max(-32768, Math.min(32767, Math.floor(floatChannel[j] * 32768)))

              // Yield periodically to prevent UI freezing
              if (j > 0 && j % samplesPerYield === 0) {
                await new Promise(resolve => setTimeout(resolve, 0))
              }
            }
            int16Samples.push(int16)
          }

          // Create MP3 encoder
          const mp3encoder = new lamejs.Mp3Encoder(
            channels, // number of channels
            sampleRate, // sample rate
            bitRate // bit rate
          )

          const mp3Data = []
          const sampleBlockSize = 1152 // Standard block size for MP3 encoding

          // Encode in blocks with progress tracking
          // For long recordings, yield after EVERY block to prevent UI freezing
          const totalSamples = int16Samples[0].length
          const isLongRecording = totalSamples > 1000000
          const blocksPerYield = isLongRecording ? 1 : 5 // Yield after every block for long recordings

          console.log('Starting MP3 encoding:', {
            totalSamples,
            estimatedDuration: (totalSamples / sampleRate).toFixed(2) + 's',
            blocksPerYield,
            isLongRecording
          })

          let processedBlocks = 0
          for (let i = 0; i < totalSamples; i += sampleBlockSize) {
            processedBlocks++

            // Yield to main thread frequently to prevent freezing
            // For long recordings, yield after every block to keep UI responsive
            if (processedBlocks % blocksPerYield === 0) {
              // Use requestIdleCallback if available, otherwise setTimeout
              await new Promise(resolve => {
                if (window.requestIdleCallback) {
                  window.requestIdleCallback(() => resolve(), { timeout: 1 })
                } else {
                  setTimeout(resolve, 0)
                }
              })

              // Log progress for long recordings
              if (isLongRecording && processedBlocks % 100 === 0) {
                const progress = ((i / totalSamples) * 100).toFixed(1)
                console.log(`Encoding progress: ${progress}%`)
              }
            }

            // Prepare channel chunks
            const channelChunks = int16Samples.map(channel =>
              channel.subarray(i, i + sampleBlockSize)
            )

            // Encode the block
            let mp3buf
            if (channels === 1) {
              // Mono
              mp3buf = mp3encoder.encodeBuffer(channelChunks[0])
            } else if (channels === 2) {
              // Stereo
              mp3buf = mp3encoder.encodeBuffer(channelChunks[0], channelChunks[1])
            }

            // Add to MP3 data if buffer is not empty
            if (mp3buf.length > 0) {
              mp3Data.push(mp3buf)
            }
          }

          // Flush remaining data
          const mp3buf = mp3encoder.flush()
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf)
          }

          // Create final MP3 blob
          const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' })
          resolve(mp3Blob)
        } catch (error) {
          console.error('Conversion error:', error)
          reject(error)
        }
      }

      reader.onerror = (error) => reject(error)

      // Read the WebM blob
      reader.readAsArrayBuffer(webmBlob)
    })
  }
}

async function handleWebmToMp3Conversion (webmBlob) {
  // Check if lamejs is available
  if (typeof lamejs === 'undefined') {
    console.error('lamejs library is required. Please include lame.all.js')
    return null
  }

  let audioBlob = webmBlob

  // Validate input
  if (!audioBlob || !(audioBlob instanceof Blob)) {
    console.error('Invalid blob provided for conversion')
    return null
  }

  const fileSizeMB = audioBlob.size / (1024 * 1024)
  const estimatedDurationMinutes = fileSizeMB * 2 // Rough estimate: ~2MB per minute at typical bitrates

  try {
    console.log('Starting conversion of audio blob', {
      size: audioBlob.size,
      sizeMB: fileSizeMB.toFixed(2),
      type: audioBlob.type,
      estimatedDurationMinutes: estimatedDurationMinutes.toFixed(1)
    })

    // Warn about very large files but don't block them
    if (fileSizeMB > 100) {
      console.warn('Large file detected. Conversion may take several minutes and use significant memory.')
    }

    // Add a progress indicator or loading state here
    const mp3Data = await WebmToMp3Converter.convert(audioBlob, {
      bitRate: 128
    })

    if (!mp3Data || mp3Data.size === 0) {
      console.error('Conversion produced empty or invalid MP3 blob')
      return null
    }

    console.log('Conversion successful. MP3 Blob created:', {
      size: mp3Data.size,
      sizeMB: (mp3Data.size / (1024 * 1024)).toFixed(2),
      type: mp3Data.type,
      compressionRatio: ((1 - mp3Data.size / audioBlob.size) * 100).toFixed(1) + '%'
    })

    return mp3Data
  } catch (error) {
    console.error('Conversion failed:', error)
    // Log more details about the error
    if (error.message) {
      console.error('Error message:', error.message)
    }
    if (error.stack) {
      console.error('Error stack:', error.stack)
    }
    return null
  }
}

export { handleWebmToMp3Conversion }
